import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchPublisherAnalytics, fetchUsersAnalytics } from '@/store/User';
import { dayNames, monthNames } from '@/utils/Names';
import { RawUserSummaryData, SummaryData, UserSummaryItem, TimeRange, ChangeDirection } from '../constants/types';

export const useDeveloperSummaryData = () => {
  const [developerTimeRange, setDeveloperTimeRange] = useState<TimeRange>('yearly');
  const [rawDeveloperSummaryData, setRawDeveloperSummaryData] = useState<RawUserSummaryData>({ previous: [], current: [] });

  const dispatch = useDispatch<any>();

  useEffect(() => {
    const fetchSummaryData = async () => {
      const timeRange = developerTimeRange;
      const res = await dispatch(fetchPublisherAnalytics({ timeRange }));
      const raw = res.payload.data;
      const half = raw.length / 2;
      setRawDeveloperSummaryData({
        previous: raw.slice(0, half),
        current: raw.slice(half),
      });
    };
    fetchSummaryData();
  }, [dispatch, developerTimeRange]);

  const developerSummaryData: SummaryData[] = useMemo(() => {
    const prevArr = rawDeveloperSummaryData.previous;
    const currArr = rawDeveloperSummaryData.current;
    if (prevArr.length === 0 || currArr.length === 0) return [];

    const keys = ['total_publisher', 'males', 'females', 'others'] as const;
    type DataKey = (typeof keys)[number];

    const labelsMap: Record<DataKey, string> = {
      total_publisher: 'Total Developers',
      males: 'Males',
      females: 'Females',
      others: 'Others',
    };

    const sumByKey = (arr: UserSummaryItem[], key: DataKey) =>
      arr.reduce((acc, item) => acc + Number(item[key]), 0);

    return keys.map((key) => {
      const prev = sumByKey(prevArr, key);
      const curr = sumByKey(currArr, key);

      let direction: ChangeDirection = 'neutral';
      let pct = 0;

      if (curr > prev) {
        direction = 'up';
        pct = prev === 0 ? 100 : ((curr - prev) / prev) * 100;
      } else if (curr < prev) {
        direction = 'down';
        pct = prev === 0 ? 0 : ((prev - curr) / prev) * 100;
      }

      const pctRounded = Number(pct.toFixed(2));
      const pctString = direction === 'up' ? `+${pctRounded}%` : direction === 'down' ? `-${pctRounded}%` : `0%`;

      return { label: labelsMap[key], value: curr, pct: pctRounded, pctString, direction };
    });
  }, [rawDeveloperSummaryData]);

  const developerChartData = useMemo(() => {
    const data = rawDeveloperSummaryData.current;
    if (!data || data.length === 0) return [];

    return data.map((item) => {
      const d = new Date(item.period);
      switch (developerTimeRange) {
        case 'yearly':
          return { label: monthNames[d.getMonth()], male: +item.males, female: +item.females, others: +item.others };
        case 'monthly':
          return { label: d.getDate().toString(), male: +item.males, female: +item.females, others: +item.others };
        case 'weekly':
          return { label: dayNames[d.getDay()], male: +item.males, female: +item.females, others: +item.others };
        case 'daily':
          return { label: `${d.getHours()}`, male: +item.males, female: +item.females, others: +item.others };
        default:
          return { label: item.period, male: +item.males, female: +item.females, others: +item.others };
      }
    });
  }, [rawDeveloperSummaryData, developerTimeRange]);

  return { developerSummaryData, developerChartData, developerTimeRange, setDeveloperTimeRange };
};
